import gsap from 'gsap'

import FPSLimiter from '@/webgl/FPSLimiter'
import { clamp01, mix, randomFloat } from '@/webgl/math'
import { CollectEvent } from '@/webgl/runner/core/GameEvents'
import { CollectStatus } from '@/webgl/runner/entities/CollectibleEntity'
import { TimeEntity } from '@/webgl/runner/entities/Time'
import GameWorld from '@/webgl/runner/GameWorld'
import { ISystem } from '@/webgl/runner/interfaces/ISystem'
import Postprocess from '@/webgl/runner/rendering/Postprocess'

export default class PostprocessSystem implements ISystem {
  private _jaugeLerped: number = 0
  consatTimeline: gsap.core.Timeline
  limiter: FPSLimiter
  glitch: number = 0

  intense: boolean = false

  grain: number = 0.0
  brightness: number = 1.0
  contrast: number = 1.0
  saturation: number = 1.0

  constructor(
    readonly world: GameWorld,
    readonly post: Postprocess
  ) {
    this.world.events.PlayCollect.on(this.onCollect)
    this.limiter = new FPSLimiter()
    this.limiter.enabled = true
    this.limiter.fps = 6
    this.enableIntense()
  }

  dispose(): void {
    this.world.events.PlayCollect.off(this.onCollect)
  }

  onCollect = (evt: CollectEvent) => {
    switch (evt.collectible.collectStatus) {
      case CollectStatus.Succeed:
        return this.succeedEffect()
      case CollectStatus.Failed:
        return this.failedEffect()
    }
  }

  enableFrozenFeature() {
    this.post.displacement.enableFrozen()
  }

  disableFrozenFeature() {
    this.post.displacement.disableFrozen()
  }

  pixelateAnimation() {
    gsap.to(this.post.pixelate, { pixelSize: 20, duration: 2.0 })
  }

  releasePixelate() {
    gsap.to(this.post.pixelate, { pixelSize: 1, duration: 1.2 })
  }

  enableFreeze() {
    gsap.to(this.post.displacement, { frozenReveal: 1, duration: 3.0 })
    gsap.to(this, { saturation: 0.85, duration: 3.0 })
  }

  disableFreeze() {
    gsap.to(this.post.displacement, { frozenReveal: 0, duration: 1.2 })
    gsap.to(this, { saturation: 1, duration: 1.2 })
  }

  succeedEffect() {
    this.consatTimeline?.kill()
    this.consatTimeline = gsap.timeline()
    this.consatTimeline
      .set(this.post.contrast, { ctR: 1 })
      .to(this, { saturation: 1.4, duration: 0.2 })
      .to(this, { brightness: 1.1, contrast: 1.1, duration: 0.2 }, '-=0.2')
      .to(this, { saturation: 1, duration: 0.4 })
      .to(this, { brightness: 1, contrast: 1, duration: 0.2 }, '-=0.4')
  }

  failedEffect() {
    this.consatTimeline?.kill()
    this.consatTimeline = gsap.timeline()
    this.consatTimeline
      .set(this, { brightness: 1, contrast: 1 })
      .to(this, { saturation: 0.5, duration: 0.1 })
      .to(this, { brightness: 0.95, contrast: 1.2, duration: 0.1 }, '-=0.1')
      .to(this.post.contrast, { ctR: 0.2, duration: 0.1 }, '-=0.1')
      .to(this, { contrast: 1, brightness: 1, saturation: 1, duration: 0.6 })
      .to(this.post.contrast, { ctR: 1, duration: 0.6 }, '-=0.4')
  }

  enableIntense() {
    this.post.grain.size = 0.25
    this.post.grain.amount = 0.0
    this.intense = true
  }

  disableIntense() {
    this.post.grain.amount = 0.0
    this.post.grain.size = 0.35
    this.intense = false
  }

  update(time: TimeEntity): void {
    const jauge = this.world.state.value.jauge

    this._jaugeLerped = mix(this._jaugeLerped, jauge, clamp01(time.deltaTime * 2.0))

    this.post.displacement.limiter.fps = mix(2, 5, this._jaugeLerped)
    this.post.displacement.displaceStrength = mix(0.01, 0.0225, this._jaugeLerped)

    let grain: number
    let brightness = this.brightness
    let contrast = this.contrast
    let saturation = this.saturation

    grain = mix(0, 2.0, this._jaugeLerped)

    if (this.intense) {
      if (this.limiter.step(time.deltaTime)) {
        this.glitch = Math.random()
        this.limiter.fps = randomFloat(0.12, 3)
      }

      grain = mix(0, 3.5, this._jaugeLerped)

      saturation = this.saturation - this._jaugeLerped * this.glitch * 0.3
      brightness = this.brightness + this._jaugeLerped * this.glitch * 0.05
      contrast = this.contrast + this._jaugeLerped * this.glitch * 0.1
    }

    this.post.grain.amount = grain
    this.post.contrast.brightness = brightness
    this.post.contrast.contrast = contrast
    this.post.saturation.amount = saturation
  }
}
